/**
 * The row menu of a draft line (round 4 of the minimal redesign, 12 Oct 2026): the three
 * dots at the end of the action cell in the table, and between "Use this" and the
 * chevron on a card. The one place a sales person changes the item of a matched row,
 * sets or changes a price, or undoes a decision without opening the details.
 *
 * Five groups, by the row's state: the item (Pick item, Change item), the quantity
 * (Correct quantity, on every engine line since the sales review of 12 Oct 2026, U-01:
 * before, the correction dialog was reachable only through the line details), the price
 * (Set price, Change price, Undo price, and for a viewer who may approve prices: Approve
 * price on a held line, Take back approval on an approved one), the decision (No item, Undo), and the deeper
 * search (Look again, only where the server offers it for the case). The details keep
 * their buttons for the same actions; nothing is duplicated in the row itself. Shown only
 * while the case is open and the line is an engine line (a line sales added has its own
 * dialog). Unlike the row button, a decided row has a menu too: "Undo" lives here.
 *
 * The DS Menu opens with Enter and Space on the trigger; the popup is in a portal, so a
 * server render carries the trigger and its label, never the items.
 */

import { Ellipsis, Icon } from "@norican/icons";
import { Button, Menu } from "@norican/react";
import { Fragment, useState } from "react";

import { type DraftCellContext } from "@/features/case-review/draft-line-cells";
import { type DraftRow } from "@/features/case-review/draft-line-groups";
import { canLookAgain } from "@/features/case-review/investigation";
import { isAddedLine } from "@/features/case-review/line-corrections";
import { effectiveItem } from "@/features/case-review/line-semantics";
import { logger } from "@/lib/logger";
import { ACTION_LABELS, LINE_STATUS_WORDS } from "@/lib/sales-flow";
import { track } from "@/lib/usage";
import { INVESTIGATION_WORDS } from "@/lib/work-labels";

export interface LineMenuEntry {
  id:
    | "pick"
    | "change-item"
    | "correct-qty"
    | "set-price"
    | "change-price"
    | "undo-price"
    | "approve-price"
    | "take-back-approval"
    | "no-item"
    | "undo"
    | "look-again";
  label: string;
  /** The spoken name when the visible word is not enough ("Undo I2Q's decision for ..."). */
  ariaLabel?: string;
  run: () => void;
}

/** Whether the row has a menu: the case is open and the line is an engine line. */
export function hasLineMenu(ctx: Pick<DraftCellContext, "detail">, row: DraftRow): boolean {
  const workOpen = (ctx.detail.workStatus ?? "open") === "open";
  return workOpen && Boolean(row.line.lineKey) && !isAddedLine(row.line) && row.lineIndex >= 0;
}

/**
 * The menu's groups for a row, empty groups left out. Pure, so a test reads the table of
 * the contract: "d" = a decision (a person's or I2Q's), "p" = a price a person set,
 * "item" = the line has a matched, suggested or decided item.
 */
export function lineMenuGroups(row: DraftRow, ctx: DraftCellContext): LineMenuEntry[][] {
  const { line, word, lineIndex } = row;
  const key = line.lineKey ?? "";
  const decided = Boolean(line.decision);
  const manualPrice = line.price?.source === "sales";
  const hasItem = Boolean(effectiveItem(line).item);
  const pick = () => ctx.onPick(line.id);

  const item: LineMenuEntry[] = [];
  if (!decided && word === LINE_STATUS_WORDS.pick) {
    item.push({ id: "pick", label: ACTION_LABELS.pickItem, run: pick });
  } else if (
    !decided &&
    (word === LINE_STATUS_WORDS.confirm || word === LINE_STATUS_WORDS.matched)
  ) {
    item.push({ id: "change-item", label: ACTION_LABELS.changeItem, run: pick });
  }

  // The quantity and unit correction (F08) for every engine line, decided or not: the
  // dialog itself shows what I2Q read and keeps "Not a line to quote" and the undo.
  const quantity: LineMenuEntry[] = [
    {
      id: "correct-qty",
      label: ACTION_LABELS.correctQuantity,
      ariaLabel: `Correct quantity or unit of: ${line.enquiryText}`,
      run: () => ctx.onCorrectQty(line.id),
    },
  ];

  const price: LineMenuEntry[] = [];
  if (hasItem) {
    price.push({
      id: manualPrice ? "change-price" : "set-price",
      label: manualPrice ? ACTION_LABELS.changePrice : ACTION_LABELS.setPrice,
      run: () => ctx.onSetPrice(line.id),
    });
  }
  if (manualPrice) {
    // A price left on a line without an item (the item was undone) can still be taken off.
    price.push({
      id: "undo-price",
      label: ACTION_LABELS.undoPrice,
      ariaLabel: `Undo the price for ${line.enquiryText}`,
      run: () => ctx.writes.undoPrice(lineIndex, key),
    });
  }
  // The approval of a price below the limit, one click on the line (ease round, 13 Oct
  // 2026: it was six clicks deep in Details). Only for a viewer who may approve prices.
  // An approval that no longer matches the line reads as none: "Approve price" again.
  if (ctx.mayApprove && line.price?.approval === "approved") {
    price.push({
      id: "take-back-approval",
      label: ACTION_LABELS.takeBackApproval,
      ariaLabel: `Take back the price approval of ${line.enquiryText}`,
      run: () => ctx.writes.approvePrice(lineIndex, false),
    });
  } else if (ctx.mayApprove && line.price?.margin === "needs_approval") {
    price.push({
      id: "approve-price",
      label: ACTION_LABELS.approvePrice,
      ariaLabel: `Approve the price of ${line.enquiryText}`,
      run: () => ctx.writes.approvePrice(lineIndex, true),
    });
  }

  const decision: LineMenuEntry[] = decided
    ? [
        {
          id: "undo",
          label: ACTION_LABELS.undo,
          ariaLabel: line.decision?.by_i2q
            ? `Undo I2Q's decision for ${line.enquiryText}`
            : `Undo the decision for ${line.enquiryText}`,
          run: () => ctx.writes.decideLine(lineIndex, key, { kind: "clear" }),
        },
      ]
    : [
        {
          id: "no-item",
          label: ACTION_LABELS.noItem,
          ariaLabel: `No item for ${line.enquiryText}`,
          run: () => ctx.writes.decideLine(lineIndex, key, { kind: "no_item" }),
        },
      ];

  // "Look again" for this one line (round 2): only where the server offers it for the
  // case, never on a line a person decided or corrected, never while a request waits.
  const search: LineMenuEntry[] = canLookAgain(ctx.detail, line)
    ? [
        {
          id: "look-again",
          label: ACTION_LABELS.lookAgain,
          ariaLabel: INVESTIGATION_WORDS.lookAgainLine(line.enquiryText),
          run: () => ctx.writes.lookAgain(key),
        },
      ]
    : [];

  return [item, quantity, price, decision, search].filter((group) => group.length > 0);
}

/** The three dots of one row; null when the row has no menu. */
export function LineMenu({ row, ctx }: { row: DraftRow; ctx: DraftCellContext }) {
  const [open, setOpen] = useState(false);
  if (!hasLineMenu(ctx, row)) {
    return null;
  }
  const { line } = row;
  const groups = lineMenuGroups(row, ctx);
  return (
    <Menu.Root
      open={open}
      onOpenChange={(next) => {
        logger.debug("[case] line menu", { case: ctx.detail.id, line: line.id, open: next });
        if (next) {
          track("case.menu_open", { menu: "line" }, { caseId: ctx.detail.id });
        }
        setOpen(next);
      }}
    >
      <Menu.Trigger
        render={
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            iconOnly
            aria-label={ACTION_LABELS.lineActions(line.enquiryText)}
            data-line-menu={line.id}
            // In the table Tab moves between the row button and this menu once the
            // focus is in the cell (the DS grid's mark for a control of a cell).
            data-norican-grid-widget=""
          />
        }
      >
        <Icon icon={Ellipsis} size="xs" />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner align="end" sideOffset={6}>
          <Menu.Popup>
            {groups.map((group, index) => (
              <Fragment key={group[0]?.id ?? index}>
                {index > 0 && <Menu.Separator />}
                {group.map((entry) => (
                  <Menu.Item
                    key={entry.id}
                    disabled={ctx.writes.busy}
                    aria-label={entry.ariaLabel}
                    data-line-menu-item={entry.id}
                    onClick={() => {
                      // The usage log: the id of the entry, never the text of the line.
                      track(
                        "case.menu_item",
                        { menu: "line", item: entry.id },
                        { caseId: ctx.detail.id },
                      );
                      entry.run();
                    }}
                  >
                    {entry.label}
                  </Menu.Item>
                ))}
              </Fragment>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
